import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Loader2, CheckCircle, XCircle, ExternalLink, AlertCircle } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { AdminDiscoveryControl } from '@/components/AdminDiscoveryControl';
import { AdminFreshnessControl } from '@/components/AdminFreshnessControl';

interface DiscoveredBot {
  id: string;
  name: string;
  url: string;
  description: string;
  status: string;
  safety_score: number;
  safety_flags: string[];
  extracted_data: any;
  enriched_data: any;
  created_at: string;
  discovery_jobs: {
    search_query: string;
    started_at: string;
  };
}

export default function Admin() {
  const { isAdmin, isAuthenticated, loading: authLoading, rolesLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [bots, setBots] = useState<DiscoveredBot[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [rejectReason, setRejectReason] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    // Only redirect if auth and roles are done loading
    if (authLoading || rolesLoading) return;
    
    if (!isAuthenticated) {
      navigate('/auth');
      toast({
        title: 'Sign in required',
        description: 'Please sign in to access the admin dashboard.',
        variant: 'destructive',
      });
      return;
    }
    
    // Check admin access after roles are loaded
    if (!isAdmin) {
      navigate('/');
      toast({
        title: 'Access Denied',
        description: 'Admin privileges required. Go to /admin-setup to grant access.',
        variant: 'destructive',
      });
    }
  }, [isAuthenticated, authLoading, rolesLoading, isAdmin, navigate, toast]);

  useEffect(() => {
    if (isAdmin) {
      fetchBots(activeTab);
    }
  }, [isAdmin, activeTab]);

  const fetchBots = async (status: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('list-discovered-bots', {
        body: { status, limit: 50, offset: 0 }
      });

      if (error) throw error;

      setBots(data.bots || []);
    } catch (error: any) {
      console.error('Error fetching bots:', error);
      toast({
        title: 'Error',
        description: 'Failed to load discovered bots',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const approveBot = async (botId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('approve-discovered-bot', {
        body: { botId }
      });

      if (error) throw error;

      toast({
        title: 'Bot Approved',
        description: `The chatbot "${data.bot.name}" has been added to the catalog and is now live on the website!`,
      });

      fetchBots(activeTab);
    } catch (error: any) {
      console.error('Error approving bot:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to approve bot',
        variant: 'destructive',
      });
    }
  };

  const rejectBot = async (botId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('reject-discovered-bot', {
        body: { botId, reason: rejectReason[botId] || '' }
      });

      if (error) throw error;

      toast({
        title: 'Bot Rejected',
        description: 'The chatbot has been rejected',
      });

      setRejectReason(prev => ({ ...prev, [botId]: '' }));
      fetchBots(activeTab);
    } catch (error: any) {
      console.error('Error rejecting bot:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to reject bot',
        variant: 'destructive',
      });
    }
  };

  if (authLoading || rolesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
        <p className="ml-3 text-muted-foreground">Loading admin dashboard...</p>
      </div>
    );
  }

  if (!isAdmin) {
    return null; // useEffect will handle redirect
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">Admin Dashboard</h1>
        <p className="text-muted-foreground">
          Review and manage discovered chatbots. Approved bots will appear live on the main website.
        </p>
      </div>

      {/* Live Freshness System */}
      <div className="mb-8">
        <AdminFreshnessControl />
      </div>

      {/* Discovery Control Panel */}
      <div className="mb-8">
        <AdminDiscoveryControl />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pending">Pending Review</TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="space-y-4 mt-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : bots.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">No bots found with status: {activeTab}</p>
              </CardContent>
            </Card>
          ) : (
            bots.map((bot) => (
              <Card key={bot.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-2xl">{bot.name}</CardTitle>
                      <CardDescription className="mt-2">
                        Discovered via: {bot.discovery_jobs?.search_query}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2 items-center">
                      {bot.safety_score > 70 ? (
                        <Badge variant="default">Safe: {bot.safety_score}%</Badge>
                      ) : (
                        <Badge variant="destructive">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          Risk: {bot.safety_score}%
                        </Badge>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(bot.url, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Visit
                      </Button>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4">
                  <div>
                    <h4 className="font-semibold mb-2">Description</h4>
                    <p className="text-sm text-muted-foreground">{bot.description}</p>
                  </div>

                  {bot.enriched_data && (
                    <>
                      {bot.enriched_data.category && (
                        <div>
                          <h4 className="font-semibold mb-2">Category</h4>
                          <Badge>{bot.enriched_data.category}</Badge>
                        </div>
                      )}

                      {bot.enriched_data.key_features && bot.enriched_data.key_features.length > 0 && (
                        <div>
                          <h4 className="font-semibold mb-2">Key Features</h4>
                          <ul className="list-disc list-inside space-y-1 text-sm">
                            {bot.enriched_data.key_features.map((feature: string, idx: number) => (
                              <li key={idx}>{feature}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-4">
                        {bot.enriched_data.pros && bot.enriched_data.pros.length > 0 && (
                          <div>
                            <h4 className="font-semibold mb-2 text-green-600">Pros</h4>
                            <ul className="list-disc list-inside space-y-1 text-sm">
                              {bot.enriched_data.pros.map((pro: string, idx: number) => (
                                <li key={idx}>{pro}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {bot.enriched_data.cons && bot.enriched_data.cons.length > 0 && (
                          <div>
                            <h4 className="font-semibold mb-2 text-red-600">Cons</h4>
                            <ul className="list-disc list-inside space-y-1 text-sm">
                              {bot.enriched_data.cons.map((con: string, idx: number) => (
                                <li key={idx}>{con}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>

                      {bot.enriched_data.pricing && (
                        <div>
                          <h4 className="font-semibold mb-2">Pricing</h4>
                          <Badge variant="outline">{bot.enriched_data.pricing}</Badge>
                        </div>
                      )}
                    </>
                  )}

                  {bot.safety_flags && bot.safety_flags.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-2 text-amber-600">Safety Flags</h4>
                      <div className="flex flex-wrap gap-2">
                        {bot.safety_flags.map((flag, idx) => (
                          <Badge key={idx} variant="outline">{flag}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {activeTab === 'pending' && (
                    <div className="flex gap-4 pt-4 border-t">
                      <div className="flex-1 space-y-2">
                        <Textarea
                          placeholder="Rejection reason (optional)"
                          value={rejectReason[bot.id] || ''}
                          onChange={(e) => setRejectReason(prev => ({ ...prev, [bot.id]: e.target.value }))}
                          rows={2}
                        />
                        <div className="flex gap-2">
                          <Button
                            onClick={() => approveBot(bot.id)}
                            className="flex-1"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Approve & Add to Catalog
                          </Button>
                          <Button
                            onClick={() => rejectBot(bot.id)}
                            variant="destructive"
                            className="flex-1"
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}